"use client";

import React, { useState, useRef, useEffect } from "react";

interface TooltipProps {
  children: React.ReactNode;
  content: string;
  multiline?: boolean;
  placement?: "top" | "bottom" | "left" | "right";
  className?: string;
  maxWidth?: "xs" | "sm" | "md" | "lg" | "xl";
}

export const Tooltip: React.FC<TooltipProps> = ({
  children,
  content,
  multiline = false,
  placement = "top",
  className = "",
  maxWidth = "sm",
}) => {
  const [isVisible, setIsVisible] = useState(false);
  const [position, setPosition] = useState({ top: 0, left: 0 });
  const [actualPlacement, setActualPlacement] = useState(placement);
  const containerRef = useRef<HTMLDivElement>(null);
  const tooltipRef = useRef<HTMLDivElement>(null);

  // Get max width class based on prop
  const getMaxWidthClass = (width: string) => {
    switch (width) {
      case "xs":
        return "max-w-xs"; // 320px
      case "sm":
        return "max-w-sm"; // 384px
      case "md":
        return "max-w-md"; // 448px
      case "lg":
        return "max-w-lg"; // 512px
      case "xl":
        return "max-w-xl"; // 576px
      default:
        return "max-w-sm";
    }
  };

  useEffect(() => {
    if (isVisible && containerRef.current && tooltipRef.current) {
      const containerRect = containerRef.current.getBoundingClientRect();
      const tooltipRect = tooltipRef.current.getBoundingClientRect();
      const viewport = {
        width: window.innerWidth,
        height: window.innerHeight,
      };

      let newTop = 0;
      let newLeft = 0;
      let newPlacement = placement;

      // Calculate initial position
      switch (placement) {
        case "top":
          newTop = containerRect.top - tooltipRect.height - 8;
          newLeft =
            containerRect.left +
            containerRect.width / 2 -
            tooltipRect.width / 2;
          break;
        case "bottom":
          newTop = containerRect.bottom + 8;
          newLeft =
            containerRect.left +
            containerRect.width / 2 -
            tooltipRect.width / 2;
          break;
        case "left":
          newTop =
            containerRect.top +
            containerRect.height / 2 -
            tooltipRect.height / 2;
          newLeft = containerRect.left - tooltipRect.width - 8;
          break;
        case "right":
          newTop =
            containerRect.top +
            containerRect.height / 2 -
            tooltipRect.height / 2;
          newLeft = containerRect.right + 8;
          break;
      }

      // Check boundaries and adjust
      if (newTop < 8) {
        newTop = containerRect.bottom + 8;
        newPlacement = "bottom";
      } else if (newTop + tooltipRect.height > viewport.height - 8) {
        newTop = containerRect.top - tooltipRect.height - 8;
        newPlacement = "top";
      }

      if (newLeft < 8) {
        newLeft = 8;
      } else if (newLeft + tooltipRect.width > viewport.width - 8) {
        newLeft = viewport.width - tooltipRect.width - 8;
      }

      setPosition({ top: newTop, left: newLeft });
      setActualPlacement(newPlacement);
    }
  }, [isVisible, placement, content]);

  const handleMouseEnter = () => {
    setIsVisible(true);
  };

  const handleMouseLeave = () => {
    setIsVisible(false);
  };

  return (
    <>
      <div
        ref={containerRef}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        className={`inline-block ${className}`}
      >
        {children}
      </div>

      {isVisible && (
        <div
          ref={tooltipRef}
          className={`fixed z-50 px-3 py-2 text-sm text-gray-900 dark:text-white bg-white dark:bg-gray-700 rounded-lg shadow-lg border border-gray-200 dark:border-gray-600 pointer-events-none ${getMaxWidthClass(
            maxWidth,
          )}`}
          style={{
            top: `${position.top}px`,
            left: `${position.left}px`,
            opacity: 1,
            transition: "opacity 0.2s ease-in-out",
          }}
        >
          {multiline ? (
            <div className="whitespace-pre-line text-left leading-relaxed break-words">
              {content}
            </div>
          ) : (
            <div className="text-left break-words">{content}</div>
          )}

          {/* Arrow */}
          <div
            className={`absolute w-2 h-2 bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 transform rotate-45 ${
              actualPlacement === "top"
                ? "top-full left-1/2 -translate-x-1/2 -mt-1 border-t-0 border-l-0"
                : actualPlacement === "bottom"
                  ? "bottom-full left-1/2 -translate-x-1/2 -mb-1 border-b-0 border-r-0"
                  : actualPlacement === "left"
                    ? "left-full top-1/2 -translate-y-1/2 -ml-1 border-l-0 border-t-0"
                    : "right-full top-1/2 -translate-y-1/2 -mr-1 border-r-0 border-b-0"
            }`}
          />
        </div>
      )}
    </>
  );
};

export const TooltipProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  return <>{children}</>;
};
